// ════════════════════════════════════════════════════════════════════════
// Coach Review · v2 — clip-track shared helpers
// ────────────────────────────────────────────────────────────────────────
// NEW (Coach Review v2 layout). The v2 clip flow puts a draggable
// IN/OUT track in the main content column. These pure helpers are
// shared by ClipTrack, ClipPresetBar, ThumbnailStrip, and FrameStepper
// so the same math (frame size, clip caps, second formatting) lives in
// one place instead of being re-derived per component.
//
// Mirrors the constants the standalone reference + the existing
// ClipComposer use:
//   - FPS = 30 — the review surface's assumed frame rate (the legacy
//     Replay coach-review also assumed 30fps for `,`/`.` frame step).
//   - MAX_CLIP_DURATION_SECONDS is re-exported from ClipComposer so
//     callers have one import; the value itself stays defined there
//     (it mirrors the backend `_MAX_CLIP_DURATION_SECONDS`).
// ════════════════════════════════════════════════════════════════════════

import { MAX_CLIP_DURATION_SECONDS } from "./components/ClipComposer";

export { MAX_CLIP_DURATION_SECONDS };

/** Review surface frame rate — drives `,` / `.` single-frame stepping. */
export const FPS = 30;

/** One frame, in seconds. */
export const FRAME = 1 / FPS;

/** Soft cap — past this the clip-track badge warns (orange) but does
 * NOT block the save. Mirrors the standalone's
 * `RECOMMENDED_CLIP_SECONDS`. */
export const RECOMMENDED_CLIP_SECONDS = 60;

/** Clamp a number into `[0, 1]`. */
export function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/** Clamp a number into `[min, max]`. */
export function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/** Format seconds as `MM:SS` (zero-padded). The clip track shows
 * short match times; for the `H:MM:SS` form past an hour use
 * `formatPlayhead` from `useVideoPlayhead`. */
export function formatClock(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Format a clip *duration* — `Ns` under a minute, `Nm SSs` past it.
 * Mirrors the standalone's `fmtDur`. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds - m * 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

/** A clip window in absolute seconds. */
export type ClipWindow = { start: number; end: number };

/** Severity of a clip window against the recommended / hard caps. */
export type ClipSeverity = "ok" | "warn" | "over";

/** Classify a window's duration: `over` past the 120s hard cap
 * (blocks save), `warn` past the 60s recommendation, else `ok`. */
export function clipSeverity(window: ClipWindow | null): ClipSeverity {
  if (!window) return "ok";
  const dur = window.end - window.start;
  if (dur > MAX_CLIP_DURATION_SECONDS) return "over";
  if (dur > RECOMMENDED_CLIP_SECONDS) return "warn";
  return "ok";
}

/** Snap a time to the nearest round second, unless an existing note
 * mark is within `snapWithin` seconds — then snap to the mark. Ports
 * the standalone's "snap to round seconds and existing note marks"
 * behavior for the IN/OUT drag handles. */
export function snapTime(
  seconds: number,
  noteMarks: ReadonlyArray<number>,
  snapWithin = 1.5,
): number {
  let best = Math.round(seconds);
  let bestDist = Math.abs(seconds - best);
  for (const mark of noteMarks) {
    const dist = Math.abs(seconds - mark);
    if (dist < bestDist && dist <= snapWithin) {
      best = mark;
      bestDist = dist;
    }
  }
  return Math.max(0, best);
}
